import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnTab from "@shared/components/SnTab";
import { global } from "@shared/libs/Global";
import SnResizeBar from "@shared/components/SnResizeBar";
import { RowNode } from "ag-grid-community";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}

// 외주발주관리
const SN_SCT_POR_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedTab, setSelectedTab] = useState("1");
  const [focusedIndex, setFocusedIndex] = useState();

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    const modifiedRowsD = gridRefs.current["grid3"].current.getChange();

    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0 ||
      modifiedRowsD.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_sct_por_reg_002_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();

      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
    } catch (error: any) {}
  };

  const onSave = async (msg?: string) => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const pohChanges = gridRefs.current["grid1"].current.getChange();
      const polChanges = gridRefs.current["grid2"].current.getChange();

      if (
        pohChanges.modifiedRowcount === 0 &&
        polChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const saveData = {
        poh: pohChanges,
        pol: polChanges,
      };

      const ok = await fnSave("SN_SCT_POR_REG_002_SAVE_01", saveData, msg);
      if (!ok) return false;

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH) {
        const result = await callApi("SN_SCT_POR_REG_002_SAVE_01_getStPo", {
          cd_firm: userInfo.cd_firm,
          no_po: focusedNodeH.data.no_po,
        });

        const status = result.status;
        const data = result.data;
        if (!status) return false;

        const st_po = data.st_po;
        if (st_po) focusedNodeH.setDataValue("st_po", st_po, "noChange");
      }

      gridRefs.current["grid1"].current?.resetUpdate();
      gridRefs.current["grid2"].current?.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid1BtnClick = async (id: string) => {
    const rowCount = gridRefs.current["grid1"].current.getDisplayedRowCount();
    if (rowCount === 0) return;

    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const displayedNodes: any[] = [];
    gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
      displayedNodes.push(node);
    });
    if (displayedNodes.length < 1) return;

    let chk = false;
    let msg = "";
    if (id == "btnRelease") {
      for (const d of displayedNodes) {
        switch (d.data.st_po) {
          case "P":
            await showDialog("alter", "확정된 건만 승인 가능합니다.");
            return;
          case "R":
            await showDialog("alter", "이미 승인 처리된 건입니다.");
            return;
          case "S":
            await showDialog("alter", "승인 처리 후 진행중인 건 입니다.");
            return;
          case "C":
            await showDialog("alter", "마감된 건 입니다.");
            return;
          case "X":
            await showDialog("alter", "전표 취소된 건 입니다.");
            return;
        }
      }

      for (const node of displayedNodes) {
        if (node.data.st_po === "F") {
          gridRefs.current["grid2"].current.setValueImmediately(
            node,
            "st_po",
            "R"
          );
          chk = true;
        }
      }
      if (!chk) {
        await showDialog("alter", "처리 실패. 미승인건이 없습니다.");
        return;
      }

      msg = "APPROVE";
      await onSave(msg);
    }
    if (id == "btnCancel") {
      if (focusedNodeH.data?.yn_app === "Y") {
        await showDialog(
          "alter",
          "발주형태 '자동승인' 건은 취소할 수 없습니다."
        );
        return;
      }

      for (const d of displayedNodes) {
        switch (d.data.st_po) {
          case "P":
          case "F":
            await showDialog("alter", "승인되지 않은 건 입니다.");
            return;
          case "S":
            await showDialog(
              "alter",
              "진행중인 건은 승인 취소할 수 없습니다.\n진행 취소 후 다시 시도하십시오."
            );
            return;
          case "C":
            await showDialog(
              "alter",
              "마감된 건은 승인 취소할 수 없습니다.\n마감 취소 후 다시 시도하십시오."
            );
            return;
          case "X":
            await showDialog("alter", "이미 전표 취소된 건 입니다.");
            return;
        }
      }

      // grid2의 qt_rcvreq 체크 => 외주입고요청
      let hasRcvReq = false;
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        if (Number(node.data.qt_rcvreq ?? 0) !== 0) {
          hasRcvReq = true;
        }
      });
      if (hasRcvReq) {
        await showDialog("alter", "진행 중인 건입니다.");
        return;
      }

      // grid3의 qt_isureq 체크 => 외주출고요청
      let hasIsuReq = false;
      gridRefs.current["grid3"].current.forEachFilteredNode((node: RowNode) => {
        if (Number(node.data.qt_isureq ?? 0) !== 0) {
          hasIsuReq = true;
        }
      });
      if (hasIsuReq) {
        await showDialog("alter", "진행 중인 건입니다.");
        return;
      }

      for (const node of displayedNodes) {
        if (node.data.st_po === "R") {
          gridRefs.current["grid2"].current.setValueImmediately(
            node,
            "st_po",
            "F"
          );
          chk = true;
          msg = "APPROVE_CANCEL";
        }
      }
      if (!chk) {
        await showDialog("alter", "처리 실패. 승인건이 없습니다.");
        return;
      }

      focusedNodeH.setDataValue("st_po", "F", "noChange");
      await onSave(msg);
    }
  };

  const onGrid2BtnClick = async (id: string) => {
    let msg = "";
    const rowCount = gridRefs.current["grid1"].current.getDisplayedRowCount();
    if (rowCount === 0) return;

    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const displayedNodes: any[] = [];
    gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
      displayedNodes.push(node);
    });
    const selectedNodes = displayedNodes.filter(
      (node: any) => node?.data && node.isSelected()
    );

    let updated = false;
    if (id == "btnRelease") {
      for (const node of selectedNodes) {
        const st_po = String(node.data.st_po ?? "");
        switch (st_po) {
          case "P":
            await showDialog("alter", "확정된 건만 승인 가능합니다.");
            return;
          case "R":
            await showDialog("alter", "이미 승인 처리된 건입니다.");
            return;
          case "S":
            await showDialog("alter", "승인 처리 후 진행중인 건 입니다.");
            return;
          case "C":
            await showDialog("alter", "마감된 건 입니다.");
            return;
          case "X":
            await showDialog("alter", "전표 취소된 건 입니다.");
            return;
        }
      }

      for (const node of selectedNodes) {
        if (node.data.st_po === "F") {
          gridRefs.current["grid2"].current.setValueImmediately(
            node,
            "st_po",
            "R"
          );
          updated = true;
          msg = "APPROVE";
        }
      }
      if (!updated) return;

      await onSave(msg);
    }
    if (id == "btnReleaseCancel") {
      if (focusedNodeH.data?.yn_app === "Y") {
        await showDialog(
          "alter",
          "발주형태 '자동승인' 건은 취소할 수 없습니다."
        );
        return;
      }

      for (const node of selectedNodes) {
        const st_po = String(node.data.st_po ?? "");
        switch (st_po) {
          case "P":
          case "F":
            await showDialog("alter", "승인되지 않은 건 입니다.");
            return;
          case "S":
            await showDialog("alter", "진행중인 건은 승인 취소할 수 없습니다.");
            return;
          case "C":
            await showDialog("alter", "마감된 건은 승인 취소할 수 없습니다.");
            return;
          case "X":
            await showDialog("alter", "이미 전표 취소된 건 입니다.");
            return;
        }
      }

      const childMap = new Map<string, any[]>();
      gridRefs.current["grid3"].current.forEachFilteredNode((node: RowNode) => {
        const key = `${node.data.cd_item}_${node.data.no_line_pol}`;
        if (!childMap.has(key)) {
          childMap.set(key, []);
        }
        childMap.get(key)!.push(node.data);
      });
      for (const node of selectedNodes) {
        const key = `${node.data.cd_item}_${node.data.no_line}`;
        const childList = childMap.get(key) ?? [];

        const hasInvalid = childList.some(
          (c) => Number(c.qt_isureq ?? 0) !== 0
        );
        if (hasInvalid) {
          await showDialog("alter", "진행 중인 건입니다.");
          return;
        }

        if (Number(node.data.qt_rcvreq ?? 0) !== 0) {
          await showDialog("alter", "진행 중인 건입니다.");
          return;
        }
      }

      for (const node of selectedNodes) {
        if (node.data.st_po === "R") {
          gridRefs.current["grid2"].current.setValueImmediately(
            node,
            "st_po",
            "F"
          );
          updated = true;
          msg = "APPROVE_CANCEL";
        }
      }
      if (!updated) return;

      await onSave(msg);
    }
    if (id == "btnClose") {
      const allStPo = selectedNodes.map((node: any) =>
        String(node.data.st_po ?? "")
      );
      const hasC = allStPo.includes("C");
      if (hasC) {
        await showDialog("alter", "이미 마감처리된 건이 존재합니다.");
        return;
      }

      for (const node of selectedNodes) {
        const qt_rcvreq = node.data.qt_rcvreq;
        const qt_rcv = node.data.qt_rcv;
        if (qt_rcvreq - qt_rcv !== 0) {
          await showDialog("alter", "입고되지 않은 건이 존재합니다.");
          return;
        }

        const dc_reason_close = String(node.data.dc_reason_close ?? "");
        const st_po = String(node.data.st_po ?? "");
        if (st_po !== "C" && dc_reason_close === "") {
          await showDialog("alter", "종결사유를 입력하시기 바랍니다.");
          gridRefs.current["grid2"].current.setFocusedCell(
            node.rowIndex,
            "dc_reason_close"
          );
          return;
        }
      }

      for (const node of selectedNodes) {
        gridRefs.current["grid2"].current.setValueImmediately(
          node,
          "st_po",
          "C"
        );
        updated = true;
        msg = "CLOSE";
      }
      if (!updated) return;

      await onSave(msg);
    }
    if (id == "btnCloseCancel") {
      const yn_app = focusedNodeH?.data?.yn_app ?? "";

      const allStPo = selectedNodes.map((node: any) =>
        String(node.data.st_po ?? "")
      );
      const hasC = allStPo.includes("C");
      if (!hasC) {
        await showDialog("alter", "마감처리 되지 않는 건이 존재합니다.");
        return;
      }

      for (const node of selectedNodes) {
        if (node.data.qt === node.data.qt_rcv) {
          await showDialog("alter", "이미 입고된 건은 마감취소가 불가합니다.");
          return;
        }

        const qt_rcvreq = Number(node.data.qt_rcvreq ?? 0);
        if (qt_rcvreq !== 0) {
          gridRefs.current["grid2"].current.setValueImmediately(
            node,
            "st_po",
            "S"
          );
        } else {
          if (yn_app === "Y") {
            gridRefs.current["grid2"].current.setValueImmediately(
              node,
              "st_po",
              "R"
            );
          } else {
            gridRefs.current["grid2"].current.setValueImmediately(
              node,
              "st_po",
              "F"
            );
          }
        }
        updated = true;
        msg = "CLOSE_CANCEL";
      }
      if (!updated) return;

      await onSave(msg);
    }
  };

  const onTabChanged = (index: string) => {
    setSelectedTab(index);
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_po = selectedRow.no_po;
      setSelectedDocu(no_po);
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: { onFocusedRowChanged: onFocusedRowChanged },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {},
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {},
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "발주정보" }}
            events={grids.grid1.events}
            filter={{
              refs: [gridRefs.current["grid2"], gridRefs.current["grid3"]],
              columns: ["no_po"],
            }}
            header={
              <div className="flex justify-end">
                <SnButton
                  text={"발주별승인"}
                  onClick={() => {
                    onGrid1BtnClick("btnRelease");
                  }}
                />
                <SnButton
                  text={"발주별승인취소"}
                  onClick={() => {
                    onGrid1BtnClick("btnCancel");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnTab
            titles={["발주내역", "소요량내역"]}
            width={"100%"}
            onTabChanged={onTabChanged}
            header={[
              <SnTabHeader
                body={
                  <div>
                    <SnButton
                      text={"승인처리"}
                      onClick={() => {
                        onGrid2BtnClick("btnRelease");
                      }}
                    />
                    <SnButton
                      text={"승인취소"}
                      onClick={() => {
                        onGrid2BtnClick("btnReleaseCancel");
                      }}
                    />
                    <SnButton
                      text={"마감처리"}
                      onClick={() => {
                        onGrid2BtnClick("btnClose");
                      }}
                    />
                    <SnButton
                      text={"마감취소"}
                      onClick={() => {
                        onGrid2BtnClick("btnCloseCancel");
                      }}
                    />
                  </div>
                }
              />,
              <SnTabHeader />,
            ]}
            body={[
              <SnGrid
                ref={gridRefs.current["grid2"]}
                columnConfig={grids.grid2.columns}
                rowData={grids.grid2.dataSource}
                etcProps={{ rowSelection: "multiple" }}
                events={grids.grid2.events}
              />,
              <SnGrid
                ref={gridRefs.current["grid3"]}
                columnConfig={grids.grid3.columns}
                rowData={grids.grid3.dataSource}
                etcProps={{ isEditable: false }}
                events={grids.grid3.events}
              />,
            ]}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_SCT_POR_REG_002;
